import React, { useState } from 'react'
import { isNull, isUndefined } from '../../utils/locDash'
import { UseUploadProps, ResultType, FileError } from './Upload.types'

const IMAGE_MIME_TYPE = 'image/*'
const VIDEO_MIME_TYPE = 'video/*'
const DOCUMENT_MIME_TYPE = 'application/*'
const INPUT_TYPE = 'file'

const useUpload = (params: UseUploadProps) => {
  const [result, setResult] = useState<ResultType<unknown>>()
  const [fileRejections, setFileRejections] = useState<{ file: File; error: FileError }>()

  const { onRequest = undefined, validator = undefined, fileType } = params

  const onChangeWithRequest = async (event: React.ChangeEvent<HTMLInputElement>) => {
    // event variable is reference type, this will ensure that the changes make do not affect the original data
    const cloneEvent = { ...event }
    const { files } = cloneEvent.target
    const file = files[0]

    if (!validation(file)) return

    const { statusCode, data } = await onRequest(file)

    if (statusCode === 200) {
      onChange(cloneEvent, data)
    }
  }

  const validation = (file: File) => {
    const error = validator?.(file) || null

    if (isNull(error)) return true

    setFileRejections({ file, error })
    return false
  }

  const getBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = (error) => reject(error)
    })

  const onChange = async ({ target }: React.ChangeEvent<HTMLInputElement>, response?: unknown) => {
    const { files } = target
    const file = files[0]

    if (!validation(file)) return

    const source = await getBase64(file)
    setResult({ source, response, file })
    target.value = ''
  }

  const onRemove = (event: React.MouseEvent) => {
    event.preventDefault()
    setResult(undefined)
  }

  const acceptFileType = {
    image: IMAGE_MIME_TYPE,
    video: VIDEO_MIME_TYPE,
    document: DOCUMENT_MIME_TYPE,
  }

  const register = {
    accept: acceptFileType[fileType],
    onChange,
    id: 'uploadFile',
    type: INPUT_TYPE,
  }

  !isUndefined(onRequest) && Object.assign(register, { onRequest: onChangeWithRequest })

  return { register, fileRejections, result, onRemove }
}

export { useUpload }
